import { RouterOutlet, Router, NavigationEnd, RouterStateSnapshot, Params } from '@angular/router';
import { ChangeDetectorRef, Component, HostBinding, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { distinctUntilChanged, filter, map, startWith, switchMap, take, tap } from 'rxjs/operators';
import { combineLatest, Observable, Subscription } from 'rxjs';

import * as version from '@assets/version.json';
import { isNullOrUndefined } from '@app/util/util';
import { AppFacade } from '@app/app.facade';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { StatsigExperimentsFacade } from '@statsig/statsig.facade';
import { STATSIG_EXPERIMENTS } from '@statsig/statsig.config';
import { AppContainerBulkService } from '@app/services/bulk-services/app.container.bulk.service';
import { DefaultTheme } from '@assets/themes/theme.types';
import { loadTheme } from '@assets/themes/helpers/switcher.helper';
import { Transitions } from '@ux/animations/animations';
import { NavigationService } from '@app/services/navigation.service';
import { HeaderType } from '@design-system/components/templates/header/entities/header.types';
import { HeaderPageType, HeaderProductType } from '@header/entities/ds-header-config';
import { UserInfo } from '@app/modules/core/interfaces/user';
import { GlobalFacade } from '@app/global.facade';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.sass'],
  encapsulation: ViewEncapsulation.None,
  animations: Transitions,
  standalone: false
})
export class AppContainer implements OnInit, OnDestroy {

  private readonly _version = version;

  @HostBinding('attr.ng-rappi-travel-version') version = this._version?.tag;
  @HostBinding('attr.released') released = this._version?.released;

  private appSubscribers: Subscription[] = [];

  public maintenancePageActive = false;

  constructor(
    private readonly facade: AppFacade,
    private readonly globalFacade: GlobalFacade,
    private readonly router: Router,
    private readonly statsigExperimentsFacade: StatsigExperimentsFacade,
    private readonly service: AppContainerBulkService,
    private readonly navigationService: NavigationService,
    private readonly cdr: ChangeDetectorRef,
    @Inject(IS_BROWSER) public isBrowser: boolean,
  ) {
    this.service.createLinkForCanonicalURL();
  }

  ngOnInit(): void {
    if (this.isBrowser) {
      loadTheme(DefaultTheme);
      this.service.setDeviceId();
      this.facade.initAssets();
      this.initListenNavigationEvents();
      this.service.initAvo();
      this.navigationService.initTransitions();
      this.navigationService.onPopState();
      this.statsigExperimentsFacade.experimentTreatmentOn$(STATSIG_EXPERIMENTS.TRAVEL_MAINTENANCE_PAGE).pipe(
        tap(treatment => this.maintenancePageActive = treatment)
      ).subscribe().unsubscribe();
    }
  }

  ngOnDestroy(): void {
    this.navigationService.stopTransitions();
  }

  get isMobile$(): Observable<boolean> {
    return this.service.isMobileDevice$;
  }

  get userInfo$(): Observable<UserInfo> {
    return this.facade.user$;
  }

  get routeAnimation(): number {
    return this.isBrowser && this.service.isMobileAgent ? this.navigationService.animationValue : null;
  }

  public notBodyScroll$(outlet: RouterOutlet): Observable<{ [key: string]: boolean }> {
    return this.isMobile$.pipe(map(isMobile => {
      const disabledScroll = isMobile && !isNullOrUndefined(this.routeAnimation) &&
        (
          (
            outlet?.activatedRouteData?.dsHeaderConfig?.type === HeaderType.Results &&
            outlet?.activatedRouteData?.dsHeaderConfig?.pageType === HeaderPageType.ResultsPage &&
            outlet?.activatedRouteData?.dsHeaderConfig?.productType === HeaderProductType.Air
          ) ||
          (
            outlet?.activatedRouteData?.dsHeaderConfig?.type === HeaderType.Generic &&
            outlet?.activatedRouteData?.dsHeaderConfig?.pageType === HeaderPageType.SearchPage
          )
        );
      return  { 'no-scroll': disabledScroll };
    }));
  }

  private initListenNavigationEvents(): void {
    this.appSubscribers.push(this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.router.routerState.snapshot),
        switchMap((snapshot) => this.facade.pageType$.pipe(
          take(1),
          map((pageType) => ({ pageType, snapshot }))
        ))
      ).subscribe(({ snapshot, pageType }) => {
        this.service.createLinkForCanonicalURL();
        const url = snapshot.url.split('?')[0];
        const extras = { queryParams: snapshot.root.queryParams };
        this.setRouterState(snapshot, pageType);
        this.facade.historyPush(url, extras);
        this.initTrackingParams(snapshot.root.queryParams);
      })
    );
  }

  public showPrimeModal$: Observable<boolean> = combineLatest([
    this.userInfo$,
    this.globalFacade.modalPrimeEnable$
  ]).pipe(
    map(([userInfo, modalPrimeEnable]) =>
      !!modalPrimeEnable && !userInfo?.is_prime
    ),
    startWith(false),
    distinctUntilChanged()
  );

  private initTrackingParams(params: Params): void {
    const { utm_source, utm_medium, utm_campaign } = params;
    if (!isNullOrUndefined(utm_source) && !isNullOrUndefined(utm_medium) && !isNullOrUndefined(utm_campaign)) {
      this.facade.setTrackingAction({ utm_source, utm_medium, utm_campaign });
    }
  }

  private setRouterState(snapshot: RouterStateSnapshot, pageType: HeaderPageType): void {
    const urlBase = decodeURIComponent(snapshot.url.split('?')[0].split('(')[0].split('%28')[0]);
    const urlParamsRef = snapshot.url.split(';');
    const commands = urlParamsRef.length > 1 && urlParamsRef[1].includes('ref') ?
      [urlParamsRef[0].split('(')[0].split('%28')[0], { ref: urlParamsRef[1].split('=')[1] }] :
      [urlBase];
    this.facade.setCurrentUrl(commands, { queryParams: snapshot.root.queryParams });
    if (pageType === HeaderPageType.HomePage) {
      this.facade.resetNavigation();
    } else {
      this.facade.addNavigationItem({
        Url: urlBase,
        PageType: pageType,
        Extras: { queryParams: snapshot.root.queryParams }
      });
    }
  }

}
